/**
 * Base API Service - shared across Metlas apps
 *
 * Provides: API URL detection, 403 handling, request/response logging,
 * withCredentials for auth cookies, and standard HTTP methods.
 */
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { API_CONFIG } from './config';
import { logger } from './logger';

/**
 * Runtime API URL detection - runs in the browser
 */
function getApiBaseUrl(): string {
  const buildTimeUrl =
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL;
  if (buildTimeUrl && buildTimeUrl !== 'undefined') {
    logger.info('Using build-time API URL:', buildTimeUrl);
    return buildTimeUrl;
  }
  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;
    logger.debug('Runtime API URL detection', { hostname, protocol });
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      logger.info('Using localhost backend');
      return 'http://localhost:3000';
    }
    const apiUrl = `${protocol}//${hostname}`;
    logger.info('Using same-origin backend via Ingress:', apiUrl);
    return apiUrl;
  }
  return 'http://localhost:3000';
}

export abstract class BaseApiService {
  protected client: AxiosInstance;
  protected serviceName: string;

  constructor(serviceName = 'API', baseURL?: string) {
    this.serviceName = serviceName;
    const apiUrl = baseURL || getApiBaseUrl();
    logger.info(`${serviceName} Base URL:`, apiUrl);

    this.client = axios.create({
      baseURL: apiUrl,
      withCredentials: true,
      timeout: API_CONFIG.TIMEOUT,
      headers: { 'Content-Type': 'application/json' },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      (config) => {
        logger.log(
          `[${this.serviceName}] Making request to:`,
          `${config.baseURL || ''}${config.url || ''}`
        );
        return config;
      },
      (error) => {
        logger.error(`[${this.serviceName}] Request error:`, error);
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response) => {
        logger.log(`[${this.serviceName}] Response received:`, response.status);
        return response;
      },
      (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          const errorMessage =
            error.response.data?.error ||
            "You don't have permission to perform this operation. Please contact your administrator to grant you this permission if it is needed.";
          logger.error(`[${this.serviceName}] Permission denied:`, errorMessage);

          const enhancedError = new Error(errorMessage) as Error & {
            response?: typeof error.response;
            isPermissionError?: boolean;
          };
          enhancedError.response = error.response;
          enhancedError.isPermissionError = true;
          return Promise.reject(enhancedError);
        }

        logger.error(`[${this.serviceName}] Request failed:`, error.message);
        return Promise.reject(error);
      }
    );
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  protected async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  protected async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  protected async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}
